import { groupBy, orderBy } from "lodash"
import { useMemo } from "react"

import { CLASS_GROUPS } from "#constants/classOptions"
import {
  CLASS_GROUP_TO_SKILL_TYPE,
  CoreSkillLevel,
  DigitalSkillLevel,
  SculptureSkillLevel,
} from "#constants/skillLevelConstants"
import { EvaluationConstructor } from "#customTypes/composedTypes/evaluationTypes"
import { StudentSkillHistory } from "#customTypes/composedTypes/studentTypes"

export type SkillHistoryForDisplay = {
  semester: string
  evaluations: EvaluationConstructor[]
  coreSkillLevel?: CoreSkillLevel
  digitalSkillLevel?: DigitalSkillLevel
  sculptureSkillLevel?: SculptureSkillLevel
  hasEvaluation: boolean
}

const getLastSemester = (semester: string): string => {
  const [year, term] = semester.split(" ")
  const lastTermMap: Record<string, string> = {
    A: "C", // Spring -> Fall
    B: "A", // Summer -> Spring
    C: "B", // Fall -> Summer (last year)
  }

  const lastTerm = lastTermMap[term]
  const lastYear = lastTerm === "C" ? parseInt(year) - 1 : year

  return `${lastYear} ${lastTerm}`
}

const DISPLAY_KEYS_BY_GROUP = {
  // For now, there is overlap
  [CLASS_GROUPS.CORE]: "coreSkillLevel",
  [CLASS_GROUPS.PAINTING]: "coreSkillLevel",
  [CLASS_GROUPS.PORTFOLIO]: "coreSkillLevel",
  [CLASS_GROUPS.DIGITAL_ART]: "digitalSkillLevel",
  [CLASS_GROUPS.SCULPTURE]: "sculptureSkillLevel",
} as const

const useSkillHistoryData = (
  skillHistory: StudentSkillHistory | undefined,
  evals: EvaluationConstructor[] | undefined,
  // Semesters that have standout moments but no evaluation/skill-history
  // entry of their own — the owner wants the full history, so these still
  // get a section (with no eval/skill badges, just the moments block).
  semestersWithMoments: string[] = []
): SkillHistoryForDisplay[] => {
  return useMemo(() => {
    if (!skillHistory && !evals && !semestersWithMoments.length) return []

    // Group evaluations by semester they were FOR
    const evalsBySemester = groupBy(evals, "semester")

    // Get all unique semesters from skill history, evaluations, and moments
    const allSemesters = new Set([
      ...Object.keys(skillHistory || {}),
      ...Object.keys(evalsBySemester),
      ...semestersWithMoments,
    ])

    const displayItems: SkillHistoryForDisplay[] = []

    // Process each semester
    for (const semester of allSemesters) {
      const skillsEntry = skillHistory?.[semester]

      // A semester with standout moments always renders, even with no eval
      // or skill-history entry — see the comment on `semestersWithMoments`.
      let shouldShow = semestersWithMoments.includes(semester)
      const result = {
        semester,
        evaluations: evalsBySemester[semester] || [],
        hasEvaluation: !!evalsBySemester[semester]?.length,
      } as SkillHistoryForDisplay

      const lastSemester = getLastSemester(semester)

      const lastSemesterEvalsByGroup = groupBy(
        evalsBySemester[lastSemester],
        "classGroup"
      )

      Object.keys(CLASS_GROUPS).forEach((group) => {
        if (
          group === CLASS_GROUPS.PAINTING ||
          group === CLASS_GROUPS.PORTFOLIO
        ) {
          return
        }

        const typesafeGroup = group as CLASS_GROUPS
        const lastEval = lastSemesterEvalsByGroup[typesafeGroup]

        const skillHistoryKey = CLASS_GROUP_TO_SKILL_TYPE[typesafeGroup]

        let skillLevel

        if (lastEval) {
          // skip because the skill was set in the previous semester
          return
        } else if (skillsEntry?.[skillHistoryKey]) {
          skillLevel = skillsEntry?.[skillHistoryKey]
          shouldShow = true
        } else {
          const currentEval = evalsBySemester[semester]?.find(
            (evaluation) => evaluation.classGroup === typesafeGroup
          )
          if (currentEval) {
            skillLevel = currentEval.currentSkill
            shouldShow = true
          }
        }

        if (skillLevel) {
          const key = DISPLAY_KEYS_BY_GROUP[typesafeGroup]

          // @ts-expect-error - casting the key is not working but this line is not an issue.
          result[key] = skillLevel
        }
      })

      if (shouldShow) {
        displayItems.push(result)
      }
    }

    // Newest first
    return orderBy(displayItems, "semester", "desc")
  }, [skillHistory, evals, semestersWithMoments])
}

export default useSkillHistoryData
